import type { Title } from '@sr-sdks/api-sdk-axios';
import { fetchTitlesPaginated } from '../../api/titles/titlesApi';
import { createBatchedLookup } from './batchedLookup';
import { csvRequestConfig } from './csvExport';

/** The titles endpoint's `@ArrayMaxSize` on `titleIds`. */
const MAX_TITLE_IDS = 100;

/** What an export reads from a row's title rather than the row's own copy. */
export type TitleDetails = Pick<Title, 'titleType' | 'year'>;

/**
 * Year and program type for an export's rows, 100 ids a request rather than
 * one `GET /titles/:id` each: a 1000-row page is 10 requests, not 1000. Make
 * one per export. A title the API doesn't return (deleted, or outside the
 * workspace) maps to `{}`, so its fields are left to the row.
 */
export const createTitleDetailsLookup = (workspaceId: string) =>
  createBatchedLookup<TitleDetails>({
    batchSize: MAX_TITLE_IDS,
    fetchBatch: async (titleIds, signal) => {
      const { items } = await fetchTitlesPaginated(
        // Fixed, not `titleIds.length`: every id fits on the one page.
        { page: 1, pageSize: MAX_TITLE_IDS, titleIds, workspaceId },
        csvRequestConfig(signal),
      );

      return items.flatMap(({ _id, titleType, year }) =>
        _id ? [[_id, { titleType, year }] as const] : [],
      );
    },
    missing: {},
  });
